import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Detective, ArrowsClockwise, Export, Spinner, Siren, ListChecks, Fingerprint, Table, ClockCounterClockwise, Fire } from "@phosphor-icons/react";
import FraudScoreBanner from "./fraud/FraudScoreBanner";
import FraudAlertsList from "./fraud/FraudAlertsList";
import FraudPageForensics from "./fraud/FraudPageForensics";
import FraudFeedbackHistory from "./fraud/FraudFeedbackHistory";
import { FraudChecklist, FraudFieldsPanel } from "./fraud/FraudPanels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Section({ icon: Icon, title, children, testId, action }) {
  return (
    <section data-testid={testId}>
      <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Icon size={18} className="text-[#002FA7]" /> {title}
        {action && <span className="ml-auto">{action}</span>}
      </h3>
      {children}
    </section>
  );
}

export default function FraudEngineTab({ claim, fraud, selectedPage, onSelectPage, showHeatmap, onToggleHeatmap }) {
  const { analysis, loading, running, starting, refresh, run } = fraud;
  const [fbVersion, setFbVersion] = useState(0);
  const claimId = claim.id;

  if (loading) return <div className="p-6 text-sm text-gray-400">Loading fraud analysis…</div>;

  const notRun = !analysis || analysis.status === "not_run";
  const canRun = claim.status === "processed" && !running;
  const onFeedback = () => { refresh(); setFbVersion(v => v + 1); };

  return (
    <div className="p-6 space-y-6" data-testid="fraud-engine-tab">
      <div className="flex items-center justify-between">
        <div>
          <p className="label-xs">Document Fraud Detection Engine</p>
          <p className="text-xs text-gray-500 mt-0.5">Forensics · Cross-document rules · Reference pricing · Scoring</p>
        </div>
        <div className="flex gap-2">
          {analysis?.status === "completed" && (
            <>
              <Button variant={showHeatmap ? "default" : "outline"} size="sm" onClick={onToggleHeatmap}
                      className={`gap-1.5 text-xs ${showHeatmap ? "bg-orange-600 hover:bg-orange-700 text-white" : ""}`} data-testid="fraud-heatmap-toggle">
                <Fire size={14} weight={showHeatmap ? "fill" : "regular"} /> {showHeatmap ? "Overlay on" : "Tamper overlay"}
              </Button>
              <Button asChild variant="outline" size="sm" className="gap-1.5 text-xs" data-testid="fraud-export-btn">
                <a href={`${API}/fraud/export/${claimId}`} target="_blank" rel="noreferrer"><Export size={14} /> Evidence Pack</a>
              </Button>
            </>
          )}
          <Button size="sm" onClick={run} disabled={!canRun || starting} className="bg-[#002FA7] hover:bg-blue-800 text-white gap-1.5 text-xs" data-testid="fraud-run-btn">
            {running ? <><Spinner size={14} className="animate-spin" /> Analyzing…</> : <><ArrowsClockwise size={14} /> {notRun ? "Run Analysis" : "Re-run"}</>}
          </Button>
        </div>
      </div>

      {notRun && !running && (
        <div className="border border-dashed border-gray-300 rounded-md p-10 text-center" data-testid="fraud-not-run">
          <Detective size={44} className="mx-auto text-gray-300 mb-3" />
          <p className="text-sm text-gray-600">Fraud analysis has not been run for this claim.</p>
          <p className="text-xs text-gray-400 mt-1">{claim.status === "processed" ? "Click Run Analysis to start." : "Process the claim with AI first."}</p>
        </div>
      )}

      {running && (
        <div className="border border-blue-200 bg-blue-50 rounded-md p-5 flex items-center gap-3" data-testid="fraud-running">
          <Spinner size={20} className="animate-spin text-[#002FA7]" />
          <p className="text-sm text-blue-900">Running PDF forensics, perceptual hashing, ELA heatmaps, rule engine and scoring…</p>
        </div>
      )}

      {analysis?.status === "failed" && (
        <div className="border border-red-200 bg-red-50 rounded-md p-4 text-sm text-red-800" data-testid="fraud-failed">Analysis failed: {analysis.error}</div>
      )}

      {analysis?.status === "completed" && (
        <>
          <FraudScoreBanner analysis={analysis} />

          <Section icon={Siren} title={`Triggered Alerts (${analysis.triggered_alerts?.length || 0})`} testId="fraud-alerts-section">
            <FraudAlertsList alerts={analysis.triggered_alerts} claimId={claimId} onSelectPage={onSelectPage} onFeedback={onFeedback} />
          </Section>

          <Section icon={ClockCounterClockwise} title="Investigator Feedback History" testId="fraud-feedback-section">
            <FraudFeedbackHistory claimId={claimId} refreshKey={fbVersion} />
          </Section>

          <Section icon={ListChecks} title="Mandatory Document Checklist" testId="fraud-checklist-section">
            <FraudChecklist missing={analysis.missing_documents} />
          </Section>

          <Section icon={Fingerprint} title="Per-Page Forensics" testId="fraud-forensics-section">
            <FraudPageForensics pages={analysis.pages} claimId={claimId} suspiciousPages={analysis.pages_suspicious} onSelectPage={onSelectPage} thresholds={analysis.thresholds} />
          </Section>

          <Section icon={Table} title="Extracted Fields & Confidence (selected page)" testId="fraud-fields-section">
            <FraudFieldsPanel fieldsDocs={analysis.extracted_fields} selectedPage={selectedPage} />
          </Section>

          {analysis.pdf_forensics?.length > 0 && (
            <Section icon={Fingerprint} title="PDF Structure & Tamper Checker Report" testId="fraud-pdf-section"
                     action={<a href={`${API}/fraud/tamper-report/${claimId}`} target="_blank" rel="noreferrer" className="text-xs font-normal text-[#002FA7] hover:underline" data-testid="tamper-json-link">Tamper JSON</a>}>
              <div className="space-y-2">
                {analysis.pdf_forensics.map((f, i) => {
                  const rep = (analysis.tamper_reports || []).find(t => t.file === f.file_name) || {};
                  const verdictCls = rep.verdict === "suspicious" ? "bg-red-600 text-white" : rep.verdict === "review" ? "bg-yellow-100 text-yellow-800" : "bg-green-50 text-green-700";
                  return (
                  <div key={i} className="border border-gray-200 rounded-md p-3 text-xs grid grid-cols-3 gap-y-1 gap-x-4" data-testid={`pdf-forensic-${i}`}>
                    <p className="col-span-3 font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
                      {f.file_name}
                      <span className={`uppercase text-[10px] ${f.risk_level === "fraud" ? "text-red-600" : f.risk_level === "warning" ? "text-yellow-700" : "text-green-700"}`}>{f.risk_level}</span>
                      {rep.verdict && (
                        <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded ${verdictCls}`} data-testid={`tamper-verdict-${i}`}>
                          Tamper score {rep.tamper_score}/100 · {rep.verdict.toUpperCase()} · {(rep.alerts || []).length} checks fired
                        </span>
                      )}
                    </p>
                    <p><span className="text-gray-400">Save layers</span> <span className={f.is_incremental_update ? "text-red-700 font-bold" : ""}>{f.eof_count}{f.is_linearized ? " (linearized)" : ""}</span></p>
                    <p><span className="text-gray-400">Fonts</span> {f.font_inventory?.length || 0}</p>
                    <p><span className="text-gray-400">Annotations</span> {f.total_annotations || 0}{f.has_acroform ? " + form" : ""}</p>
                    <p className="col-span-3 text-gray-500 truncate">{(f.font_inventory || []).join(", ") || "No embedded fonts (scanned)"}</p>
                  </div>
                  );
                })}
              </div>
            </Section>
          )}
        </>
      )}
    </div>
  );
}
